import { DateRange } from '@mui/x-date-pickers-pro';
import { Moment } from 'moment';
import { InventoryMapper } from '../../../common/inventory/mappers/InventoryMapper';
import { InventoryRecap } from '../../../common/inventory/models/InventoryRecap';
import { INVENTORY_QUERY_FRAGMENT } from '../../../common/inventory/services/InventoryService';
import { BaseGQLService } from '../../../common/services/BaseGQLService';
import { DateUtils } from '../../../common/utils/DateUtils';
import { PriorityFixMapper } from '../mappers/PriorityFixMapper';
import { SummaryMapper } from '../mappers/SummaryMapper';
import { OrganizationData } from '../models/OrganizationData';

const PRIORITY_FIXES_FRAGMENT = `
  edges {
    node {
      number
      title
      shortExplanation
      severity
      project {
        id
        name
      }
    }
  }
`;

export const DASHBOARD_TRENDS_DATA_QUERY = `
query ($tags: [String!], $projects: [String!], $profiles: [String!], $first: Int, $fromDate: DateTime!, $toDate: DateTime!, $includeExceptions: Boolean!, $overviewTypes: [OverviewType!]) {
  viewer {
    organization {
      name
      profiles(first: 1, sorters:[{field: id, order: ASC}]) {
        edges {
          node {
            createdAt
          }
        }
      }
      vulnerabilities(first: $first, tags: $tags, projectIds: $projects, profileIds: $profiles, overviewTypes: $overviewTypes, withExceptions: $includeExceptions, from: $fromDate, to: $toDate) {
        ${PRIORITY_FIXES_FRAGMENT}
      }
      inventory(tags: $tags, projectIds: $projects, profileIds: $profiles, overviewTypes: $overviewTypes, from: $fromDate, to: $toDate) {
        ${INVENTORY_QUERY_FRAGMENT}
      }
      timeRepresentationSlices(tags: $tags, projectIds: $projects, profileIds: $profiles, from: $fromDate, to: $toDate, numberOfSlices: 20, overviewTypes: $overviewTypes) {
        from
        to
        high
        medium
        low
        passed
      }
    }
  }
}`;

export class DashboardDataProvider extends BaseGQLService {
  private readonly summaryMapper = new SummaryMapper();
  private readonly priorityFixMapper = new PriorityFixMapper();
  private readonly inventoryMapper = new InventoryMapper();

  async getTrendsOrganizationData(
    tags: string[] = [],
    projects: string[] = [],
    profiles: string[] = [],
    [from, to]: DateRange<Moment>,
    includeExceptions: boolean,
    types: string[]
  ): Promise<OrganizationData> {
    return this.query(
      DASHBOARD_TRENDS_DATA_QUERY,
      {
        tags,
        projects,
        profiles,
        first: 10,
        includeExceptions,
        fromDate: from !== null ? DateUtils.toISOString(from) : null,
        toDate: to !== null ? DateUtils.toISOString(to) : null,
        overviewTypes: types,
      },
      (data) => {
        const summary = this.summaryMapper.mapToDomain(data.viewer.organization);
        const inventory = this.inventoryMapper.mapGQLModel(data.viewer.organization.inventory);
        const priorityFixes = this.priorityFixMapper.mapToDomain(data.viewer.organization.vulnerabilities.edges);

        return new OrganizationData(summary, priorityFixes, inventory ?? InventoryRecap.getBlank());
      }
    );
  }
}
